import * as names from "@/store/names";

/**
 * E55 · Guardado de la pauta que propuso la IA.
 *
 * Reproduce exactamente la cadena que hace `NewRubricForm.vue` al crear una pauta a mano
 * —pauta, modelo, niveles de desempeño, criterios y celdas— y no una versión propia: si la
 * generación armara la pauta por otro camino, quedarían dos formas de tener una pauta en la
 * base y sólo una de ellas sería la que el resto de la plataforma espera.
 *
 * El orden importa y no es casual:
 *   1. la Pauta, que es de lo que cuelga todo
 *   2. su Modelo (`rubric-type-default`), que declara con qué escala se evalúa
 *   3. los Niveles de Desempeño, copiados de las unidades del Modelo — las COLUMNAS
 *   4. los Criterios — las FILAS, una por Indicador de Logro
 *   5. las Celdas, que son lo único que escribió la IA
 *
 * Los niveles se copian del Modelo y no los propone la IA porque son una escala
 * institucional: si cada pauta tuviera la suya, los logros dejarían de ser comparables entre
 * evaluaciones.
 */

/**
 * Crea la Pauta, su Modelo y sus Niveles de Desempeño.
 *
 * Devuelve { rubric, levels_by_order } — el mapa es lo que después permite colgar cada celda
 * de su columna, porque el `order` es lo único que comparten la unidad del Modelo y el Nivel
 * recién creado.
 */
async function createRubricWithLevels(
  vm,
  { test_id, title, preset, preset_units, show_percentage_sign }
) {
  const rubric = await vm.$store.dispatch(names.POST_NEW_RUBRIC_INSTRUMENT, {
    test: test_id,
    redaction_question: null,
    title,
    description: "",
    order: 1,
    // El Modelo propone cómo se muestra el logro y el usuario puede cambiarlo, igual que en
    // el formulario manual; lo que el Modelo decide sin discusión es la escala.
    show_percentage_sign:
      show_percentage_sign != null
        ? show_percentage_sign
        : preset
        ? preset.show_percentage_sign
        : false,
    allow_criteria_performance_level_input: preset
      ? preset.allow_criteria_performance_level_input
      : true,
    show_in_tab: false,
    visible_by_evaluatees: false,
    is_locked: false,
    student_weighing: 0,
    evaluate_autoevaluation: false,
    evaluatee_can_see_coevaluator: false,
    coevaluation_type: 1,
    files: [],
  });
  if (!rubric) return null;

  await vm.$restful.Post("/evaluations2/rubric-type-default/", {
    rubric: rubric.id,
    preset: preset ? preset.id : null,
  });

  const levels_by_order = {};
  for (const unit of preset_units || []) {
    const level = await vm.$store.dispatch(names.POST_NEW_RUBRIC_ACHIEVEMENT, {
      title: unit.title,
      description: unit.description || "",
      order: unit.order,
      score: unit.score,
      rubric: rubric.id,
    });
    if (level) levels_by_order[unit.order] = level.id;
  }
  return { rubric, levels_by_order };
}

/**
 * Guarda los criterios aceptados con sus celdas.
 *
 * @param vm               el componente que llama (para $store y $restful)
 * @param test_id          el Instrumento
 * @param rubric           la pauta, si ya existe; si no, se crea
 * @param title            el nombre de la pauta nueva
 * @param preset           el Modelo de Pauta elegido (sólo al crear)
 * @param preset_units     sus unidades, que se vuelven los Niveles (sólo al crear)
 * @param levels_by_order  los niveles de una pauta que ya existe, { order: id }
 * @param criterias        los criterios aceptados, como los devolvió el builder
 * @param start_order      el `order` del primer criterio: al final de los que ya hay
 * @param on_saved         callback por criterio guardado, para el avance en pantalla
 *
 * Devuelve { rubric, created }. Un criterio que falle no corta los demás: el usuario aceptó
 * un conjunto, y dejarlo a medias sin decir cuáles entraron es peor que guardar los que se
 * pudieron.
 */
export async function saveAiRubric(
  vm,
  {
    test_id,
    rubric = null,
    title = "",
    preset = null,
    preset_units = [],
    show_percentage_sign = null,
    levels_by_order = null,
    criterias,
    single_criteria = false,
    start_order = 1,
    on_saved = null,
  }
) {
  let target_rubric = rubric;
  let levels = levels_by_order;

  if (!target_rubric) {
    const created_rubric = await createRubricWithLevels(vm, {
      test_id,
      title,
      preset,
      preset_units,
      show_percentage_sign,
    });
    if (!created_rubric) return { rubric: null, created: [] };
    target_rubric = created_rubric.rubric;
    levels = created_rubric.levels_by_order;
  }

  const created = [];
  let order = start_order;
  for (const criteria of criterias || []) {
    try {
      const saved = await vm.$store.dispatch(names.POST_NEW_RUBRIC_CRITERIA, {
        rubric: target_rubric.id,
        // `full_sentence` es la oración completa y `action` es su VERBO. No es
        // redundante: `SentenceContainer` dibuja `action` subrayado y a continuación
        // `full_sentence` SIN su primera palabra, para no repetir el verbo. Con `action`
        // vacío el criterio se mostraba decapitado —"el rol del comercio exterior..."
        // en vez de "Reconoce el rol del comercio exterior..."—.
        //
        // La FK `verb` la resuelve el backend por nombre contra los verbos de la
        // institución; es la que pinta el nivel cognitivo. Cuando el verbo que escribió
        // la IA no está en esa lista queda en null: el criterio igual se muestra
        // completo, sólo sin color.
        verb: criteria.verb || null,
        action: criteria.action || "",
        content: "",
        condition: "",
        full_sentence: criteria.title,
        weighing: criteria.weighing,
        order,
        // Los dos campos del Indicador: la FK es la que el modelo usa para enganchar el
        // logro (`RubricCriteria._get_ecmi_previous_and_current_assessments`) y el arreglo
        // es el que muestra la tabla. Con uno solo, el criterio queda amarrado a medias.
        //
        // Una dimensión puede medir varios Indicadores; la FK sólo admite uno, así que lleva
        // el primero y el arreglo los lleva todos. Es la misma asimetría que tiene el
        // formulario manual, no una decisión de acá.
        //
        // `single_criteria` es el caso de las instituciones que evalúan a través de la
        // Dimensión: ahí la dimensión mide UN Indicador y sólo la FK se llena. Sus 425
        // dimensiones existentes están así —FK puesta, arreglo vacío—, y llenar el arreglo
        // metría la dimensión en la cobertura por dos caminos a la vez.
        evaluation_criteria_micro:
          (criteria.evaluation_criterias_micro || [])[0] || null,
        evaluation_criterias_micro: single_criteria
          ? []
          : criteria.evaluation_criterias_micro || [],
      });
      if (!saved) continue;

      for (const cell of criteria.cells || []) {
        const level_id = (levels || {})[cell.level_order];
        if (level_id == null) continue;
        await vm.$store.dispatch(names.POST_NEW_RUBRIC_DESCRIPTOR, {
          // El texto visible de la celda vive en `title`, no en `description`: es el campo
          // que edita y muestra `RubricDescriptorCell.vue`.
          title: cell.description,
          description: "",
          rubric_criteria: saved.id,
          rubric_performance_level: level_id,
        });
      }

      created.push({ ...criteria, id: saved.id });
      order += 1;
      if (on_saved) on_saved(created.length);
    } catch (error) {
      // Se sigue con el próximo: el detalle del fallo ya lo reportó `restful`.
      continue;
    }
  }
  return { rubric: target_rubric, created };
}
